import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Link2, Copy, Trash2, Eye, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useApp } from "@/context/AppContext";
import { Panel, Field, Empty } from "@/components/kit";
import { dmy, errMsg } from "@/lib/format";

export const DemoLinks = () => {
  const { api, t } = useApp();
  const [rows, setRows] = useState(null);
  const [leads, setLeads] = useState([]);
  const [f, setF] = useState({ firm_name: "", message: "", days: 30 });
  const logo = useRef();
  const load = useCallback(() => {
    api.get("/demo/links").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/demo/leads").then((r) => setLeads(r.data)).catch(() => setLeads([]));
  }, [api]);
  useEffect(() => { load(); }, [load]);
  const url = (s) => `${window.location.origin}/demo/${s}`;
  const create = async () => {
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    if (logo.current?.files[0]) fd.append("logo", logo.current.files[0]);
    try {
      const { data } = await api.post("/demo/links", fd);
      navigator.clipboard?.writeText(url(data.slug));
      toast.success(t("demo_link_created"));
      setF({ firm_name: f.firm_name, message: "", days: 30 }); if (logo.current) logo.current.value = "";
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const revoke = async (l) => { if (window.confirm(t("delete"))) { await api.delete(`/demo/links/${l.id}`); load(); } };
  return (
    <Panel title={t("demo_links")} testid="firm-demo-links">
      <p className="mb-4 text-sm text-slate-500">{t("demo_links_help")}</p>
      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_120px]">
        <Field label={t("demo_firm_name")}><Input value={f.firm_name} onChange={(e) => setF({ ...f, firm_name: e.target.value })} data-testid="demo-link-firm" /></Field>
        <Field label={t("demo_logo")}><Input ref={logo} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" data-testid="demo-link-logo" /></Field>
        <Field label={t("demo_days")}><Input type="number" min="1" max="365" value={f.days} onChange={(e) => setF({ ...f, days: e.target.value })} data-testid="demo-link-days" /></Field>
      </div>
      <Field label={t("demo_message")} className="mt-3"><Textarea rows={2} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder={t("demo_message_ph")} data-testid="demo-link-message" /></Field>
      <Button className="mt-3" onClick={create} disabled={!f.firm_name.trim()} data-testid="demo-link-create"><Link2 className="h-4 w-4 me-1" />{t("demo_link_create")}</Button>
      <div className="mt-6">
        {rows && rows.length === 0 ? <Empty text={t("demo_links_empty")} /> : (rows || []).map((l) => {
          const expired = l.revoked || l.expires_at < new Date().toISOString();
          return (
            <div key={l.id} className={`flex flex-wrap items-center gap-3 border-b border-slate-50 py-3 text-sm ${expired ? "opacity-50" : ""}`} data-testid={`demo-link-row-${l.slug}`}>
              <div className="min-w-0 flex-1"><div className="font-medium">{l.firm_name}</div><div className="truncate num text-xs text-slate-400" dir="ltr">{url(l.slug)}</div></div>
              <span className="flex items-center gap-1 text-xs text-slate-600" data-testid={`demo-link-views-${l.slug}`}><Eye className="h-3.5 w-3.5" />{l.views}</span>
              <span className="flex items-center gap-1 text-xs text-emerald-700" data-testid={`demo-link-signups-${l.slug}`}><UserPlus className="h-3.5 w-3.5" />{l.signups}</span>
              <span className="text-xs text-slate-500">{l.revoked ? t("demo_revoked") : `${t("demo_until")} ${dmy(l.expires_at)}`}</span>
              {!expired && <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard?.writeText(url(l.slug)); toast.success(t("copied")); }} data-testid={`demo-link-copy-${l.slug}`}><Copy className="h-4 w-4" /></Button>}
              {!l.revoked && <Button size="sm" variant="ghost" onClick={() => revoke(l)} data-testid={`demo-link-revoke-${l.slug}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
            </div>
          );
        })}
      </div>
      {leads.length > 0 && (
        <div className="mt-6" data-testid="demo-leads">
          <div className="eyebrow mb-2">{t("demo_leads")} ({leads.length})</div>
          {leads.map((l) => (
            <div key={l.id} className="flex flex-wrap items-center gap-3 border-b border-slate-50 py-2 text-sm" data-testid={`demo-lead-${l.email}`}>
              <span className="font-medium" dir="ltr">{l.email}</span><span className="text-xs text-slate-400">{l.firm_name} · {dmy(l.created_at)}</span>
              <span className="ms-auto text-xs">{l.converted ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">{t("demo_lead_converted")}</span> : `${t("demo_lead_reminders")} : ${l.reminders.length}/2`}</span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
};
